import { Controller } from "@hotwired/stimulus"

// The MOV / MP4 slots. Each uploads over XHR with a progress bar so a large
// master never rides along with the form post.
export default class extends Controller {
  static targets = [
    "input", "filename", "progress", "progressBar", "error", "slot",
    "progressLabel", "previewButton", "removeButton", "fileIcon", "browseLabel",
    "modal", "player", "playerTitle", "playerError", "playerDownload", "playerNote"
  ]
  static values = {
    uploadUrl: String,
    removeUrl: String,
    urlEndpoint: String,
    unsupportedText: String,
    removeConfirm: String
  }

  browse(event) {
    const slot = event.currentTarget.dataset.slot
    const input = this.inputTargets.find(target => target.dataset.slot === slot)
    if (input) input.click()
  }

  fileSelected(event) {
    const input = event.currentTarget
    const slot = input.dataset.slot
    const file = input.files[0]
    if (!file) return

    this.hideError(slot)
    this.upload(slot, file)
  }

  // A master takes minutes. While one is in flight the page must not be left,
  // and Save and Continue must not be pressed - the form controller listens for
  // these two events to disable itself.
  static UPLOAD_STARTED = "music-video:upload-started"
  static UPLOAD_FINISHED = "music-video:upload-finished"

  uploadStarted() {
    this.activeUploads = (this.activeUploads || 0) + 1

    if (this.activeUploads === 1) {
      this.leaveGuard = event => {
        // Browsers show their own wording here and ignore ours; returnValue is
        // what actually triggers the prompt.
        event.preventDefault()
        event.returnValue = ""
        return ""
      }
      window.addEventListener("beforeunload", this.leaveGuard)
    }

    document.dispatchEvent(new CustomEvent(this.constructor.UPLOAD_STARTED))
  }

  uploadFinished() {
    this.activeUploads = Math.max((this.activeUploads || 1) - 1, 0)

    if (this.activeUploads === 0 && this.leaveGuard) {
      window.removeEventListener("beforeunload", this.leaveGuard)
      this.leaveGuard = null
    }

    document.dispatchEvent(new CustomEvent(this.constructor.UPLOAD_FINISHED))
  }

  // A page left mid-upload should not leave the guard attached to the window.
  disconnect() {
    if (this.leaveGuard) {
      window.removeEventListener("beforeunload", this.leaveGuard)
      this.leaveGuard = null
    }
    if (this.activeUploads) {
      this.activeUploads = 0
      document.dispatchEvent(new CustomEvent(this.constructor.UPLOAD_FINISHED))
    }
  }

  upload(slot, file) {
    const formData = new FormData()
    formData.append("video", file)
    formData.append("slot", slot)

    const xhr = new XMLHttpRequest()
    xhr.open("POST", this.uploadUrlValue, true)
    xhr.setRequestHeader("X-CSRF-Token", document.querySelector('meta[name="csrf-token"]').content)
    xhr.setRequestHeader("Accept", "application/json")

    this.showProgress(slot, 0)
    this.uploadStarted()

    xhr.upload.addEventListener("progress", event => {
      if (event.lengthComputable) {
        this.showProgress(slot, Math.round((event.loaded / event.total) * 100))
      }
    })

    // Once the browser has sent everything, the wait is the server writing to
    // storage - a stretch nothing can measure, so the bar says what it is doing
    // instead of sitting at 100% looking stuck.
    xhr.upload.addEventListener("load", () => this.showStoring(slot))

    xhr.onload = () => {
      this.hideProgress(slot)
      this.clearInput(slot)
      this.uploadFinished()

      let payload = {}
      try {
        payload = JSON.parse(xhr.responseText)
      } catch (e) {
        return this.showError(slot, this.t("music_videos.edit.validation.video_failed"))
      }

      if (xhr.status >= 200 && xhr.status < 300 && payload.success) {
        this.setSlotState(slot, true, `${payload.filename} (${this.humanSize(payload.size)})`)
      } else {
        this.showError(slot, (payload.errors || []).join(", ") || this.t("music_videos.edit.validation.video_failed"))
      }
    }

    xhr.onerror = () => {
      this.hideProgress(slot)
      this.clearInput(slot)
      this.uploadFinished()
      this.showError(slot, this.t("music_videos.edit.validation.video_failed"))
    }

    xhr.onabort = () => {
      this.hideProgress(slot)
      this.clearInput(slot)
      this.uploadFinished()
    }

    xhr.send(formData)
  }

  async remove(event) {
    const slot = event.currentTarget.dataset.slot

    // Deleting a master cannot be undone from here, so confirm it.
    if (this.hasRemoveConfirmValue && !window.confirm(this.removeConfirmValue)) return

    const response = await fetch(`${this.removeUrlValue}?slot=${slot}`, {
      method: "DELETE",
      headers: {
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
        "Accept": "application/json"
      }
    })

    if (response.ok) {
      this.setSlotState(slot, false)
    } else {
      this.showError(slot, this.t("music_videos.edit.validation.video_failed"))
    }
  }

  // A slot has exactly two looks: holding a file, or empty. Deleting one has to
  // put every part of it back - the filename, its colour, the film icon and the
  // button's wording - not just hide the icons.
  setSlotState(slot, present, filenameText = null) {
    const filename = this.targetForSlot("filename", slot)
    if (filename) {
      filename.textContent = present ? filenameText : (filename.dataset.emptyText || "")
      filename.classList.toggle("text-gray-900", present)
      filename.classList.toggle("text-gray-400", !present)
    }

    const icon = this.targetForSlot("fileIcon", slot)
    if (icon) {
      icon.classList.toggle("text-gray-700", present)
      icon.classList.toggle("text-gray-300", !present)
    }

    const label = this.targetForSlot("browseLabel", slot)
    if (label) {
      label.textContent = present ? label.dataset.replaceText : label.dataset.uploadText
    }

    const slotElement = this.targetForSlot("slot", slot)
    if (slotElement) slotElement.dataset.hasFile = present ? "true" : "false"

    this.toggleSlotButtons(slot, present)
    this.hideError(slot)
    this.hideProgress(slot)
    this.clearInput(slot)

    document.dispatchEvent(new CustomEvent("music-video:files-changed"))
  }

  toggleSlotButtons(slot, present) {
    const preview = this.previewButtonTargets.find(button => button.dataset.slot === slot)
    const remove = this.removeButtonTargets.find(button => button.dataset.slot === slot)
    if (preview) preview.classList.toggle("hidden", !present)
    if (remove) remove.classList.toggle("hidden", !present)
  }

  // Fetches a fresh signed URL and plays it in the modal. MOV is quicktime and
  // most browsers refuse it, so a failure to load shows the reason plus a link
  // to open the file directly.
  async preview(event) {
    const slot = event.currentTarget.dataset.slot
    if (!this.hasModalTarget || !this.hasUrlEndpointValue) return

    this.hidePlayerError()

    try {
      const response = await fetch(`${this.urlEndpointValue}?slot=${slot}`, {
        headers: { "Accept": "application/json" }
      })
      const payload = await response.json()

      if (!response.ok || !payload.success) {
        return this.showPlayerError(payload.error || this.t("music_videos.edit.preview_failed"))
      }

      this.openModal(payload, slot)
    } catch (error) {
      this.showPlayerError(this.t("music_videos.edit.preview_failed"))
    }
  }

  openModal(payload, slot) {
    this.hidePlayerNote()
    if (this.hasPlayerTitleTarget) this.playerTitleTarget.textContent = payload.filename || ""
    if (this.hasPlayerDownloadTarget) this.playerDownloadTarget.href = payload.url

    if (this.hasPlayerTarget) {
      // Plenty of MOV files do play - Chrome handles h264 in a quicktime
      // container fine. So the Safari caveat waits until playback actually
      // fails rather than greeting every MOV with a warning it may not need.
      this.playerTarget.onerror = () => this.showPlaybackFailure(slot)
      this.playerTarget.src = payload.url
      this.playerTarget.load()
    }

    this.modalTarget.classList.remove("hidden")
    document.body.classList.add("overflow-hidden")
  }

  showPlaybackFailure(slot) {
    if (slot === "mov" && this.hasPlayerNoteTarget) {
      this.playerNoteTarget.classList.remove("hidden")
      return
    }

    this.showPlayerError(this.unsupportedTextValue)
  }

  hidePlayerNote() {
    if (this.hasPlayerNoteTarget) this.playerNoteTarget.classList.add("hidden")
  }

  closePreview() {
    if (this.hasPlayerTarget) {
      this.playerTarget.pause()
      this.playerTarget.removeAttribute("src")
      this.playerTarget.load()
    }

    this.hidePlayerError()
    this.hidePlayerNote()
    this.modalTarget.classList.add("hidden")
    document.body.classList.remove("overflow-hidden")
  }

  showPlayerError(message) {
    if (!this.hasPlayerErrorTarget) return

    this.playerErrorTarget.textContent = message
    this.playerErrorTarget.classList.remove("hidden")
    this.modalTarget.classList.remove("hidden")
    document.body.classList.add("overflow-hidden")
  }

  hidePlayerError() {
    if (!this.hasPlayerErrorTarget) return

    this.playerErrorTarget.textContent = ""
    this.playerErrorTarget.classList.add("hidden")
  }

  targetForSlot(name, slot) {
    return this[`${name}Targets`].find(target => target.dataset.slot === slot)
  }

  clearInput(slot) {
    const input = this.targetForSlot("input", slot)
    if (input) input.value = ""
  }

  showProgress(slot, percent) {
    const progress = this.targetForSlot("progress", slot)
    const bar = this.targetForSlot("progressBar", slot)
    const label = this.targetForSlot("progressLabel", slot)

    if (progress) progress.classList.remove("hidden")
    if (bar) {
      bar.classList.remove("animate-pulse")
      bar.style.width = `${percent}%`
    }
    if (label) label.textContent = this.t("music_videos.edit.uploading_percent", { percent })
  }

  showStoring(slot) {
    const bar = this.targetForSlot("progressBar", slot)
    const label = this.targetForSlot("progressLabel", slot)

    if (bar) {
      bar.style.width = "100%"
      bar.classList.add("animate-pulse")
    }
    if (label) label.textContent = this.t("music_videos.edit.storing")
  }

  hideProgress(slot) {
    const progress = this.targetForSlot("progress", slot)
    const bar = this.targetForSlot("progressBar", slot)
    const label = this.targetForSlot("progressLabel", slot)

    if (progress) progress.classList.add("hidden")
    if (bar) {
      bar.classList.remove("animate-pulse")
      bar.style.width = "0%"
    }
    if (label) label.textContent = ""
  }

  showError(slot, message) {
    const element = this.targetForSlot("error", slot)
    if (!element) return

    element.textContent = message
    element.classList.remove("hidden")
  }

  hideError(slot) {
    const element = this.targetForSlot("error", slot)
    if (!element) return

    element.textContent = ""
    element.classList.add("hidden")
  }

  humanSize(bytes) {
    if (!bytes) return ""

    const units = ["B", "KB", "MB", "GB"]
    let value = bytes
    let unit = 0
    while (value >= 1024 && unit < units.length - 1) {
      value /= 1024
      unit += 1
    }
    return `${value.toFixed(1)} ${units[unit]}`
  }

  t(key, options = {}) {
    if (window.I18n && typeof window.I18n.t === "function") {
      return window.I18n.t(key, options)
    }
    return key
  }
}
